import { useRef, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { stoneIcon, visitorIcon } from '../utils/leafletIcons'

function BoundsFitter({ visitorLocation, stoneLat, stoneLng }) {
  const map = useMap()
  const fitted = useRef(false)
  useEffect(() => {
    if (visitorLocation && !fitted.current) {
      map.fitBounds([[visitorLocation.lat, visitorLocation.lng], [stoneLat, stoneLng]], { padding: [50, 50] })
      fitted.current = true
    }
  }, [visitorLocation, stoneLat, stoneLng, map])
  return null
}

function defaultPopup(stone) {
  return (
    <div style={{ minWidth: 140 }}>
      {stone.photoUrl && (
        <img src={stone.photoUrl} alt="Stone" style={{ width: '100%', borderRadius: 4, marginBottom: 8 }} />
      )}
      <strong>{stone.name || 'Unknown'}</strong>
      {stone.deathDate && <p style={{ margin: '4px 0', fontSize: 12 }}>d. {stone.deathDate}</p>}
      {stone.gps_accuracy_m != null && (
        <p style={{ margin: '4px 0', fontSize: 11, color: '#666' }}>GPS: {Number(stone.gps_accuracy_m).toFixed(1)}m accuracy</p>
      )}
    </div>
  )
}

// The single map surface for every Granite Graph tool that shows stones on a
// map — browsing many at once (the cemetery-wide Map view) or navigating to
// one (public Search, field tool search mode). One implementation instead of
// three independently-drifting copies. See "Shared Core, Many Views" in
// docs/architecture/decisions.md.
//
// Props:
// - stones: array of { stone_id, lat, lng, name, deathDate, photoUrl, gps_accuracy_m, condition } — see utils/mapData.js
// - visitorLocation: optional { lat, lng } — draws a "you are here" marker
// - showRoute: when true and there's exactly one stone plus a visitorLocation,
//   draws a dashed line to it and fits the view to both points
// - renderPopup(stone): optional custom marker popup; defaults to photo/name/death date/GPS accuracy
export default function StoneMap({
  stones,
  visitorLocation = null,
  showRoute = false,
  height = '300px',
  zoom = 18,
  renderPopup = defaultPopup,
}) {
  if (!stones?.length) return null
  const single = stones.length === 1 ? stones[0] : null
  const center = [stones[0].lat, stones[0].lng]

  return (
    <div style={{ height, borderRadius: '8px', overflow: 'hidden' }}>
      <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {showRoute && single && (
          <BoundsFitter visitorLocation={visitorLocation} stoneLat={single.lat} stoneLng={single.lng} />
        )}
        {stones.map(stone => (
          <Marker key={stone.stone_id} position={[stone.lat, stone.lng]} icon={stoneIcon}>
            <Popup>{renderPopup(stone)}</Popup>
          </Marker>
        ))}
        {visitorLocation && (
          <Marker position={[visitorLocation.lat, visitorLocation.lng]} icon={visitorIcon}>
            <Popup>You are here</Popup>
          </Marker>
        )}
        {showRoute && single && visitorLocation && (
          <Polyline
            positions={[[visitorLocation.lat, visitorLocation.lng], [single.lat, single.lng]]}
            pathOptions={{ color: '#60a5fa', weight: 2, dashArray: '6 6', opacity: 0.85 }}
          />
        )}
      </MapContainer>
    </div>
  )
}
